import { FilterValueWithMode } from "@impero-com/ui/domain";
import { Row, Table } from "@tanstack/react-table";

import { OnlyAt } from "api/generated/model";

import {
    HeatMapData,
    INHERENT_MODE,
    MapMode,
} from "./components/RiskHeatmapTypes";
import {
    Delta,
    MAP_INSIGHTS_COLUMN_IDS,
    RiskInsightsTableRow,
} from "./mapInsightsModel";
import { extractDeltaValue, extractStartValue } from "./mapInsightsUtils";

export function buildHeatMapData(
    table: Table<RiskInsightsTableRow>,
    mapMode: MapMode
): [HeatMapData, HeatMapData] {
    const focusColumn = table.getColumn(MAP_INSIGHTS_COLUMN_IDS.FOCUS);
    if (!focusColumn) {
        return [{}, {}];
    }

    const allRows = focusColumn.getFacetedRowModel().flatRows;
    const focusRows = table.getPrePaginationRowModel().flatRows;

    const getValues = (row: RiskInsightsTableRow) =>
        mapMode === INHERENT_MODE
            ? { impact: row.inherentImpact, likelihood: row.inherentLikelihood }
            : {
                  impact: row.residualImpact,
                  likelihood: row.residualLikelihood,
              };

    const buildData = (
        extractVal: <T>(delta: Delta<T>) => T,
        include: (onlyAt: OnlyAt | null) => boolean
    ): HeatMapData => {
        const result: HeatMapData = {};
        const increment = (
            row: Row<RiskInsightsTableRow>,
            key: "all" | "focus"
        ) => {
            if (!include(row.original.onlyAt)) {
                return;
            }
            const { impact, likelihood } = getValues(row.original);
            const impactVal = extractVal(impact);
            const likelihoodVal = extractVal(likelihood);
            if (impactVal === null || likelihoodVal === null) {
                return;
            }
            const xIdx = likelihoodVal.index;
            const yIdx = impactVal.index;
            if (result[xIdx] === undefined) {
                result[xIdx] = {};
            }
            if (result[xIdx][yIdx] === undefined) {
                result[xIdx][yIdx] = { focus: 0, all: 0 };
            }
            result[xIdx][yIdx][key]++;
        };
        for (const row of allRows) {
            increment(row, "all");
        }
        for (const row of focusRows) {
            increment(row, "focus");
        }
        return result;
    };

    const endData = buildData(
        extractDeltaValue,
        (onlyAt) => onlyAt !== "Start"
    );
    const startData = buildData(
        extractStartValue,
        (onlyAt) => onlyAt !== "End"
    );

    return [endData, startData];
}

type FocusFilterValue = FilterValueWithMode<string[]> | undefined;

export function getFocusFilterValue(
    table: Table<RiskInsightsTableRow>
): FocusFilterValue {
    return table
        .getColumn(MAP_INSIGHTS_COLUMN_IDS.FOCUS)
        ?.getFilterValue() as FocusFilterValue;
}

// Focus filter values encode a heatmap cell as the string `${x}:${y}`.
export function focusPairKey(x: number, y: number): string {
    return `${x}:${y}`;
}

export function parseFocusPairKey(pair: string): { x: number; y: number } {
    const sep = pair.indexOf(":");
    return {
        x: parseInt(pair.slice(0, sep), 10),
        y: parseInt(pair.slice(sep + 1), 10),
    };
}

function toFocusFilterValue(pairKeys: string[]): FocusFilterValue {
    return pairKeys.length === 0 ? undefined : { mode: "is", value: pairKeys };
}

export function parseFocusPairs(
    value: FocusFilterValue
): { x: number; y: number }[] {
    return (value?.value ?? []).map(parseFocusPairKey);
}

// Add the pair when missing, remove it when already present.
export function toggleFocusPair(
    value: FocusFilterValue,
    x: number,
    y: number
): FocusFilterValue {
    const current = value?.value ?? [];
    const key = focusPairKey(x, y);
    const next = current.includes(key)
        ? current.filter((p) => p !== key)
        : [...current, key];
    return toFocusFilterValue(next);
}

export function removeFocusPair(
    value: FocusFilterValue,
    x: number,
    y: number
): FocusFilterValue {
    const current = value?.value ?? [];
    const next = current.filter((p) => p !== focusPairKey(x, y));
    return toFocusFilterValue(next);
}
